import { Tabs, Typography, TreeList } from '@scale/scaleui';
import { Select } from '@scale/scaleui-radix';
import { useState } from 'react';
import {
  ChatApiEndpointWithId,
  ProvidedModelResponse,
  DEFAULT_MODEL_SELECTOR_TITLE,
  DEFAULT_MODEL_SELECTOR_SUBTITLE,
  UIGenerationEnforcementMode,
  InteractionStepType,
  ProjectChatApiEndpointWithId,
} from '@scale/shared/types/chat';
import _ from 'lodash';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import NumberInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/NumberInputBuilder';
import ChatApiEndpointsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ChatApiEndpointsBuilder';
import ProvidedModelResponsesBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ProvidedModelResponsesBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import { ReviewLevel } from '@scale/shared/defaults';
import OverrideIDBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/OverrideIDBuilder';
import {
  EnableRTLTextConfig,
  ConditionalStepConfig,
  ReviewLevelConfig,
  ToolParsingConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import LikertScaleConfig from './config/LikertConfig';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import { ExperimentalFeature } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigLifeCycleInfo';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';

const ConfigTabs = {
  General: 'General',
  Models: 'Models and Responses',
  Visibility: 'Review Level Visibility',
  ConditionalVisibility: 'Conditional Visibility',
  Tools: 'Tools',
};
type ConfigTab = keyof typeof ConfigTabs;

export function ModelResponseSelectorConfigurationComponent({
  params: currentParams,
  previousParams,
  setParams: setCurrentParams,
  taskParams,
}: StepConfigurationComponentProps<InteractionStepType.ModelResponseSelector>) {
  const [tab, setTab] = useState<ConfigTab>('General');
  const canBeConsensus = !_.isNil(currentParams.params.visibility_restricted)
    ? currentParams.params.visibility_restricted.includes(ReviewLevel.ReviewConsensus)
    : true;
  return (
    <div className="flex flex-col gap-4">
      <Tabs
        tabs={Object.entries(ConfigTabs).map(([tab, id]) => ({ id, name: tab }))}
        value={tab}
        onChange={t => setTab(t as ConfigTab)}
        variant="default"
      />

      {tab === ConfigTabs.General && (
        <div>
          <div className="mb-4">
            <StringInputBuilder
              name={'Title'}
              placeholder={DEFAULT_MODEL_SELECTOR_TITLE}
              existingValue={currentParams.title}
              updateProperty={(value: string) =>
                setCurrentParams({ ...currentParams, title: value })
              }
              required={true}
            />
          </div>
          <div className="mb-4">
            <OverrideIDBuilder
              params={currentParams}
              previousParams={previousParams}
              setParams={setCurrentParams}
            />
          </div>
          <div className="mb-4">
            <StringInputBuilder
              name={'Subtitle'}
              placeholder={DEFAULT_MODEL_SELECTOR_SUBTITLE}
              existingValue={currentParams.subtitle}
              updateProperty={(value: string) =>
                setCurrentParams({ ...currentParams, subtitle: value })
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography.Subtitle2>Additional Questions</Typography.Subtitle2>
            <BooleanInputBuilder
              name={'Ask For Subjectivity/Objectivity (would 9 out of 10 people agree?)'}
              existingValue={!!currentParams.params.show_objectivity}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, show_objectivity: value },
                })
              }
            />
            <LikertScaleConfig params={currentParams} setParams={setCurrentParams} />
            <BooleanInputBuilder
              name={'Collect Justifications for Response Selection'}
              existingValue={!!currentParams.params.show_justification}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, show_justification: value },
                })
              }
            />
            <Typography.Subtitle2>Interaction Flow</Typography.Subtitle2>
            <div className="flex flex-col gap-2">
              <BooleanInputBuilder
                name={'Reset Chat History on Selection Change'}
                existingValue={!!currentParams.params.reset_history_on_change}
                updateProperty={(value: boolean) =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, reset_history_on_change: value },
                  })
                }
              />
              <div className="prose text-sm italic ml-8">
                If checked, updating the selection for this step will delete the existing chat
                history after the current turn. This should be used when the chat history for future
                turns is dependent on the new selection. This cannot be used on L4.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <BooleanInputBuilder
                name={'Skip Selection and Hide Step'}
                existingValue={!!currentParams.params.skip_selection}
                updateProperty={(value: boolean) =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, skip_selection: value },
                  })
                }
              />
              <div className="prose text-sm italic ml-8">
                If checked, taskers will not see the model selector step. This is primarily used to
                load the model responses in the background and pass them down to the next steps.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <BooleanInputBuilder
                name={'Skip Selection but Show Step'}
                existingValue={!!currentParams.params.disable_selection_show_step}
                updateProperty={(value: boolean) =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, disable_selection_show_step: value },
                  })
                }
              />
              <div className="prose text-sm italic ml-8">
                If checked, taskers will see the model selector step but will not be able to select
                a response.
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <BooleanInputBuilder
                name={'Allow Rerolling Model Responses'}
                existingValue={!!currentParams.params.allow_reroll}
                updateProperty={(value: boolean) =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, allow_reroll: value },
                  })
                }
              />
              <div className="prose text-sm italic ml-8">
                If checked, taskers will be able to reroll the model responses to see different
                responses. This is primarily used for cases where the model responses are not good
                enough, and taskers need to see more options.
              </div>
              {currentParams.params.allow_reroll && (
                <TreeList>
                  <div>
                    <BooleanInputBuilder
                      name={'Enable For Reviewers Only'}
                      existingValue={!!currentParams.params.allow_reroll_for_reviewers_only}
                      updateProperty={(value: boolean) =>
                        setCurrentParams({
                          ...currentParams,
                          params: {
                            ...currentParams.params,
                            allow_reroll_for_reviewers_only: value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <BooleanInputBuilder
                      name={'Reroll Individual Model Response'}
                      existingValue={!!currentParams.params.allow_reroll_for_individual_candidates}
                      updateProperty={(value: boolean) =>
                        setCurrentParams({
                          ...currentParams,
                          params: {
                            ...currentParams.params,
                            allow_reroll_for_individual_candidates: value,
                          },
                        })
                      }
                    />
                    <div className="prose text-sm italic ml-8">
                      If checked, contributors will be able to reroll one model response at a time
                      instead of rerolling all model responses.
                    </div>
                  </div>
                </TreeList>
              )}
            </div>
            <Typography.Subtitle2>Additional Features</Typography.Subtitle2>
            <EnableRTLTextConfig
              value={currentParams.params.enable_rtl_text}
              onChange={value => {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, enable_rtl_text: value },
                });
              }}
            />
            <BooleanInputBuilder
              name={'[Advanced] Enable Model Critique'}
              existingValue={!!currentParams.params.enable_model_critique}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, enable_model_critique: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              If checked, this enables model critique functionality. This allows for automatic
              evaluation and feedback on model responses, potentially improving the quality of
              selections.
            </div>
            <BooleanInputBuilder
              name={'[Advanced] Omit Attachments from Prompt'}
              existingValue={!!currentParams.params.omit_attachments}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, omit_attachments: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              If checked, any attachments in the prompt will be omitted when generating the model
              response(s). Does not do anything if the prompt does not include attachments.
            </div>

            <ExperimentalFeature
              name="Enable Selection with Errors"
              details="This feature allows taskers to select a response even if some model responses have errors."
              showInfoCondition={!!currentParams.params.enable_selection_with_errors}
            >
              <div className="mb-4">
                <BooleanInputBuilder
                  name="[Advanced] Enable selection with errors"
                  existingValue={!!currentParams.params.enable_selection_with_errors}
                  updateProperty={(value: boolean) =>
                    setCurrentParams({
                      ...currentParams,
                      params: { ...currentParams.params, enable_selection_with_errors: value },
                    })
                  }
                />
                <div className="prose text-sm italic ml-8">
                  If checked, taskers will be able to select a response even if some model responses
                  have errors (one model response must succeed however). This must be used within
                  Maximum Number of Model Responses that can be in error state (when checked).
                </div>
              </div>
            </ExperimentalFeature>

            {currentParams.params.enable_selection_with_errors && (
              <>
                <NumberInputBuilder
                  name={'[Advanced] Maximum Number of Model Responses that can be in error state'}
                  existingValue={currentParams.params.max_model_response_errors_for_selection}
                  updateProperty={(value: number) =>
                    setCurrentParams({
                      ...currentParams,
                      params: {
                        ...currentParams.params,
                        max_model_response_errors_for_selection: value,
                      },
                    })
                  }
                />
                <div className="prose text-sm italic ml-8">
                  If the number of model responses in error state exceeds this value, taskers will
                  not be able to select a response. This is used with Enable selection with errors.
                  Regardless of the value of this field, taskers will not be able to select a
                  response if all model responses are in error state.
                </div>
              </>
            )}

            <ExperimentalFeature
              name="Enable UI generation"
              details="If enabled, allows contributors to render a UI of the code"
              showInfoCondition={!!currentParams.params.enable_ui_generation}
            >
              <div className="flex flex-col gap-2">
                <BooleanInputBuilder
                  name="Enable UI generation"
                  existingValue={!!currentParams.params.enable_ui_generation}
                  updateProperty={(value: boolean) =>
                    setCurrentParams({
                      ...currentParams,
                      params: { ...currentParams.params, enable_ui_generation: value },
                    })
                  }
                />
                <div className="prose text-sm italic ml-8">
                  If enabled, allows contributors to render a UI of the code
                </div>
              </div>
            </ExperimentalFeature>
            {!!currentParams.params.enable_ui_generation && (
              <div className="flex flex-col gap-2">
                <Typography.Subtitle2>UI generation enforcement mode</Typography.Subtitle2>
                <Select.Root
                  size="2"
                  value={
                    currentParams.params.ui_generation_enforcement_mode ??
                    UIGenerationEnforcementMode.None
                  }
                  onValueChange={(value: string) =>
                    setCurrentParams({
                      ...currentParams,
                      params: {
                        ...currentParams.params,
                        ui_generation_enforcement_mode: value as UIGenerationEnforcementMode,
                      },
                    })
                  }
                >
                  <Select.Trigger placeholder="Select UI generation enforcement mode" />
                  <Select.Content position="popper">
                    <Select.Item value={UIGenerationEnforcementMode.None}>None</Select.Item>
                    <Select.Item value={UIGenerationEnforcementMode.CompilationAttempted}>
                      Must Attempt Compilation
                    </Select.Item>
                    <Select.Item value={UIGenerationEnforcementMode.CompilationSuccessful}>
                      Must Compile Successfully
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              </div>
            )}

            <ExperimentalFeature
              name="Extract model response regex"
              details="If present, the model response will be extracted using the provided regex. This does not change the chat history"
              showInfoCondition={!!currentParams.params.extract_model_response_regex}
            >
              <div className="flex flex-col gap-2">
                <StringInputBuilder
                  name="Extract model response regex"
                  existingValue={currentParams.params.extract_model_response_regex || ''}
                  updateProperty={(value: string) =>
                    setCurrentParams({
                      ...currentParams,
                      params: { ...currentParams.params, extract_model_response_regex: value },
                    })
                  }
                />
                <div className="prose text-sm italic ml-8">
                  If present, the model response will be extracted using the provided regex. This
                  only affects the frontend display and not the chat history
                </div>
              </div>
            </ExperimentalFeature>

            <ExperimentalFeature
              name="Append Additional System Prompt"
              details="If present, the string here will be appended to the end of the system prompt."
              showInfoCondition={!!currentParams.params.appended_system_prompt}
            >
              <StringInputBuilder
                name="Append Additional System Prompt"
                existingValue={currentParams.params.appended_system_prompt}
                updateProperty={(value: string) => {
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, appended_system_prompt: value },
                  });
                }}
              />
              <div className="prose text-sm italic">
                If present, the string here will be appended to the end of the system prompt. This
                only works with the normal system prompt, not the GPT-only system prompt.
              </div>
            </ExperimentalFeature>
            <StringInputBuilder
              name={'[Advanced] Append Additional Instruction to the Most Recent User Message'}
              existingValue={currentParams.params.appended_user_message}
              updateProperty={(value: string) => {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, appended_user_message: value },
                });
              }}
            />
            <div className="prose text-sm italic">
              This message will be appended to the end of the most recent prompt. Typically, this is
              used in a similar way as a system prompt (but is more flexible, as some models do not
              support system prompt parameters).
            </div>
          </div>
        </div>
      )}
      {tab === ConfigTabs.Models && (
        <>
          <div className="flex flex-col gap-2">
            <ChatApiEndpointsBuilder
              existingValue={currentParams.params.chat_models}
              updateProperty={(
                value: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>,
              ) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, chat_models: value },
                })
              }
            />
          </div>
          {!!currentParams.params.provided_model_responses?.length && (
            <ProvidedModelResponsesBuilder
              typeBuilderMap={typeBuilderMap}
              name="Provided Model Responses [Deprecated please populate this info under Initial Messages!]"
              description="Add, remove, or edit fixed responses to the prompt. These are predefined and do not change with the prompt."
              existingValue={currentParams.params.provided_model_responses}
              updateProperty={(value: Array<ProvidedModelResponse>) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, provided_model_responses: value },
                })
              }
            />
          )}
          <div className="flex flex-col gap-2">
            <BooleanInputBuilder
              name={'Include Most Recently Submitted Response as Selectable Option'}
              existingValue={!!currentParams.params.use_existing_model_response_as_option}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, use_existing_model_response_as_option: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              If checked, the most recent (usually human) response will be shown as one of the
              responses that taskers can choose from when completing this step. This is primarily
              used to pass user written responses from one review level to the next.
            </div>
          </div>
          {currentParams.params.use_existing_model_response_as_option && (
            <TreeList>
              {[
                <div
                  className="flex flex-col gap-2"
                  key="include-all-responses-in-most-recent-response"
                >
                  <ExperimentalFeature
                    name="Include All Responses"
                    details="This option is highly experimental and customized for goldfish rubrics i18n"
                    showInfoCondition={
                      !!currentParams.params.include_all_responses_from_most_recent_response
                    }
                  >
                    <BooleanInputBuilder
                      name={
                        'Include All Responses in the Most Recently Submitted Response Step as Selectable Options'
                      }
                      existingValue={
                        !!currentParams.params.include_all_responses_from_most_recent_response
                      }
                      updateProperty={(value: boolean) =>
                        setCurrentParams({
                          ...currentParams,
                          params: {
                            ...currentParams.params,
                            include_all_responses_from_most_recent_response: value,
                          },
                        })
                      }
                    />
                    <div className="prose text-sm italic ml-8">
                      When this is enabled, all selected and unselected responses from the most
                      recently submitted response step will be shown as selectable options.
                    </div>
                  </ExperimentalFeature>
                </div>,
              ]}
            </TreeList>
          )}
          <div className="flex flex-col gap-2">
            <BooleanInputBuilder
              name={'Use Previous Model Responses and Positions'}
              existingValue={!!currentParams.params.use_last_model_responses}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, use_last_model_responses: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              When this is enabled, the model responses from the previous model response selector
              will be used, and new responses will not be generated. This is necessary for model
              randomization to work correctly.
            </div>
          </div>
          {currentParams.params.use_last_model_responses && (
            <TreeList>
              {[
                <div className="flex flex-col gap-2" key="replace-last-selected-candidate">
                  <BooleanInputBuilder
                    name={'Replace Last Selected Candidate with Edited Response'}
                    existingValue={
                      !!currentParams.params.replace_last_selected_candidate_with_existing
                    }
                    updateProperty={(value: boolean) =>
                      setCurrentParams({
                        ...currentParams,
                        params: {
                          ...currentParams.params,
                          replace_last_selected_candidate_with_existing: value,
                        },
                      })
                    }
                  />
                  <div className="prose text-sm italic ml-8">
                    When this is enabled, the last selected candidate will be replaced with the
                    edited response.
                  </div>
                </div>,
              ]}
            </TreeList>
          )}
          {canBeConsensus && (
            <div className="flex flex-col gap-2">
              <BooleanInputBuilder
                name={'[For Consensus] Show Consistent Model Responses'}
                existingValue={!!currentParams.params.force_consistent_responses}
                updateProperty={(value: boolean) =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, force_consistent_responses: value },
                  })
                }
              />
              <div className="prose text-sm italic ml-8">
                Always turn this on if this is a consensus step. When checked, this config ensures
                that all consensus taskers see the same model response.
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <BooleanInputBuilder
              name={'[Advanced] Exclude System Prompt in Model Request'}
              existingValue={!!currentParams.params.exclude_system_prompt}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, exclude_system_prompt: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              If selected, the system prompt will not be included in the model request.
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <BooleanInputBuilder
              name={'[Advanced] Only Use Existing Initial Messages'}
              existingValue={!!currentParams.params.only_use_existing_initial_messages}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, only_use_existing_initial_messages: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              If selected, only the initial messages, if they exist for the turn, will be used. This
              is used if you want the first few turns in a conversation to use preloaded messages,
              while future turns should use generated model responses.
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <BooleanInputBuilder
              name={'[Advanced] Skip Initial Messages'}
              existingValue={!!currentParams.params.skip_initial_messages}
              updateProperty={(value: boolean) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, skip_initial_messages: value },
                })
              }
            />
            <div className="prose text-sm italic ml-8">
              If selected, the initial messages on the config level will be skipped. This is useful
              when you have multiple model response selector steps and only want the initial
              messages to be applied to the first selector for example.
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <StringInputBuilder
              name={'[Advanced] Always Pass Down the Below Response'}
              existingValue={currentParams.params.always_send_given_id}
              updateProperty={(value: string) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, always_send_given_id: value },
                })
              }
            />
            <div className="prose text-sm italic">
              Regardless of which response the tasker selects, this config will pass down a specific
              response ID to be used in later steps. This is often used in pipelines where even if
              the model response is better than the human response, we want to pass the human
              response down to future taskers for comparison.
            </div>
          </div>
        </>
      )}
      {tab === ConfigTabs.Visibility && (
        <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      )}
      {tab === ConfigTabs.ConditionalVisibility && (
        <ConditionalStepConfig
          currentParams={currentParams}
          setCurrentParams={setCurrentParams}
          previousParams={previousParams}
          taskParams={taskParams}
        />
      )}
      {tab === ConfigTabs.Tools && (
        <div className="flex flex-col gap-2">
          <ToolParsingConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
        </div>
      )}
    </div>
  );
}
